import { useState, useEffect, useRef } from "react";
import { rankCities, type RankCity } from "../lib/authClient";

// Shared DataForSEO-backed city typeahead (used by the Organic Rank Tracker and the LLM Visibility
// Scanner). A DataForSEO location_name is comma-joined ("Montville,New Jersey,United States");
// prettyLoc renders it with spaces, compactLoc keeps just the first two segments (city, region).
export const prettyLoc = (n: string) => n.replace(/,/g, ", ");
export const compactLoc = (n: string) => n.split(",").slice(0, 2).map(s => s.trim()).join(", ");
const CITY_TYPE_TAG: Record<string, string> = { City: "City", "DMA Region": "Metro", Region: "Region" };

// Strict city typeahead backed by DataForSEO's location library, filtered by `country`. Empty = no
// pick. Editing the text clears any prior pick (the value must come from the list). Pass a STABLE
// `value` object (component state, not a fresh literal each render) so typing isn't reset.
export function CityPicker({ country, value, onChange, isDark, disabled, inpStyle }: {
  country: string; value: { name: string; code: number } | null;
  onChange: (v: { name: string; code: number } | null) => void;
  isDark: boolean; disabled?: boolean; inpStyle: React.CSSProperties;
}) {
  const [query, setQuery] = useState(value ? prettyLoc(value.name) : "");
  const [sugs, setSugs] = useState<RankCity[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const reqSeq = useRef(0);

  useEffect(() => { setQuery(value ? prettyLoc(value.name) : ""); }, [value]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  useEffect(() => {
    const q = query.trim();
    if (disabled || !country) { setSugs([]); return; }
    if (value && prettyLoc(value.name) === query) { setSugs([]); return; } // showing the picked value
    if (q.length < 2) { setSugs([]); return; }
    const seq = ++reqSeq.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try { const r = await rankCities(country, q); if (seq === reqSeq.current) { setSugs(r); setOpen(true); setActive(-1); } }
      catch { if (seq === reqSeq.current) setSugs([]); }
      finally { if (seq === reqSeq.current) setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, country, disabled, value]);

  const ink = isDark ? "#e6edf6" : "#0f172a";
  const ink2 = isDark ? "#93a4c0" : "#64748b";
  const inkMuted = isDark ? "#7c8aa0" : "#94a3b8";
  const panelBg = isDark ? "#16223a" : "#fff";
  const bdr = isDark ? "rgba(255,255,255,.16)" : "#e2e8f0";
  const activeBg = isDark ? "rgba(234,88,12,.18)" : "#fff7ed";

  const choose = (c: RankCity) => { onChange({ name: c.name, code: c.code }); setQuery(prettyLoc(c.name)); setOpen(false); setSugs([]); };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open || !sugs.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, sugs.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { if (active >= 0 && active < sugs.length) { e.preventDefault(); choose(sugs[active]); } }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <input
        value={query} disabled={disabled}
        onChange={e => { setQuery(e.target.value); if (value) onChange(null); }}
        onFocus={() => { if (sugs.length) setOpen(true); }}
        onKeyDown={onKeyDown}
        placeholder="Start typing a city…" style={inpStyle} />
      {value && <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", color: isDark ? "#86efac" : "#16a34a", fontSize: 13, pointerEvents: "none" }}>✓</span>}
      {open && (query.trim().length >= 2) && (
        <div style={{ position: "absolute", top: "calc(100% + 3px)", left: 0, right: 0, zIndex: 80, background: panelBg, border: `1px solid ${bdr}`, borderRadius: 8, boxShadow: isDark ? "0 10px 26px rgba(0,0,0,.5)" : "0 10px 26px rgba(15,23,42,.14)", maxHeight: 260, overflowY: "auto" }}>
          {loading && !sugs.length ? (
            <div style={{ padding: "9px 12px", fontSize: 12, color: inkMuted }}>Searching…</div>
          ) : sugs.length === 0 ? (
            <div style={{ padding: "9px 12px", fontSize: 12, color: inkMuted }}>No matching cities in DataForSEO for this country.</div>
          ) : sugs.map((c, i) => (
            <div key={c.code} onMouseDown={e => { e.preventDefault(); choose(c); }} onMouseEnter={() => setActive(i)}
              style={{ padding: "7px 12px", cursor: "pointer", background: i === active ? activeBg : "transparent", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 12.5, color: ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{prettyLoc(c.name)}</span>
              <span style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: .4, color: ink2, border: `1px solid ${bdr}`, borderRadius: 4, padding: "1px 5px", flexShrink: 0 }}>{CITY_TYPE_TAG[c.type] ?? c.type}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
